import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { pdfColors, pdfFontFamily, pdfFontSize, pdfLetter, pdfRadius, pdfSpacing } from '../theme';

interface KVProps {
  label: string;
  value?: React.ReactNode;
  mono?: boolean;
  /** Label uppercase + letter-spaced — matches section labels. Use inside
   *  boxed meta grids. */
  loud?: boolean;
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    paddingVertical: 3.5,
  },
  label: {
    width: 118,
    fontSize: pdfFontSize.sm,
    color: pdfColors.textMuted,
    fontWeight: 500,
    paddingRight: pdfSpacing.sm,
  },
  labelLoud: {
    fontSize: pdfFontSize.xxs,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: pdfLetter.openLabel,
    color: pdfColors.textMuted,
  },
  value: {
    flex: 1,
    fontSize: pdfFontSize.sm,
    color: pdfColors.textPrimary,
    fontWeight: 500,
  },
  valueMono: {
    fontFamily: pdfFontFamily.mono,
  },
});

/** Classic key-value row. */
export const KV: React.FC<KVProps> = ({ label, value, mono, loud }) => (
  <View style={styles.row} wrap={false}>
    <Text style={[styles.label, loud ? styles.labelLoud : null].filter(Boolean) as any}>
      {loud ? label.toUpperCase() : label}
    </Text>
    {typeof value === 'string' || typeof value === 'number' ? (
      <Text style={mono ? [styles.value, styles.valueMono] : styles.value}>
        {value === '' || value == null ? '—' : String(value)}
      </Text>
    ) : (
      <View style={{ flex: 1 }}>{value ?? <Text style={styles.value}>—</Text>}</View>
    )}
  </View>
);

export interface KVGridItem {
  label: string;
  value?: React.ReactNode;
  mono?: boolean;
  /** Claim the whole row (addresses, long free text). */
  full?: boolean;
}

interface KVGridProps {
  columns?: 2 | 3;
  items: KVGridItem[];
}

const gridStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: pdfColors.rule,
    borderBottomStyle: 'solid',
  },
  cell: {
    paddingVertical: 6,
    paddingRight: pdfSpacing.md,
  },
  label: {
    fontSize: pdfFontSize.xxs,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: pdfLetter.openLabel,
    color: pdfColors.textMuted,
    marginBottom: 2,
  },
  value: {
    fontSize: pdfFontSize.md,
    color: pdfColors.textPrimary,
    fontWeight: 500,
  },
  valueMono: {
    fontFamily: pdfFontFamily.mono,
    fontSize: pdfFontSize.sm,
  },
});

/** Record grid — label stacked over value, N per row, hairline between
 *  rows. Each row moves to the next page whole; the grid itself flows. */
export const KVGrid: React.FC<KVGridProps> = ({ columns = 2, items }) => {
  const rows: KVGridItem[][] = [];
  let bucket: KVGridItem[] = [];
  for (const it of items) {
    if (it.full) {
      if (bucket.length) { rows.push(bucket); bucket = []; }
      rows.push([it]);
    } else {
      bucket.push(it);
      if (bucket.length === columns) { rows.push(bucket); bucket = []; }
    }
  }
  if (bucket.length) rows.push(bucket);

  const cellWidth = `${100 / columns}%`;
  return (
    <View>
      {rows.map((row, r) => (
        <View key={r} style={gridStyles.row} wrap={false}>
          {row.map((it, i) => (
            <View key={i} style={[gridStyles.cell, { width: it.full ? '100%' : cellWidth }]}>
              <Text style={gridStyles.label}>{it.label}</Text>
              {typeof it.value === 'string' || typeof it.value === 'number' || it.value == null ? (
                <Text style={it.mono ? [gridStyles.value, gridStyles.valueMono] : gridStyles.value}>
                  {it.value === '' || it.value == null ? '—' : String(it.value)}
                </Text>
              ) : (
                it.value
              )}
            </View>
          ))}
        </View>
      ))}
    </View>
  );
};

// ─── Boxed meta grid ─────────────────────────────────────────────────────
// Matches the backend fee-voucher "meta grid" block: tiled labels + values
// inside a soft slate card. Great above a document body for anchoring
// context (session, due date, period, …).

interface MetaBoxProps {
  items: Array<{ label: string; value?: React.ReactNode; mono?: boolean }>;
  /** Columns — defaults to 4 on wide docs, 3 for narrower ones. */
  columns?: 2 | 3 | 4;
}

const metaStyles = StyleSheet.create({
  card: {
    borderWidth: 0.6,
    borderColor: pdfColors.divider,
    borderStyle: 'solid',
    backgroundColor: pdfColors.surfaceSoft,
    borderRadius: pdfRadius.lg,
    paddingVertical: 10,
    paddingHorizontal: 14,
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: pdfSpacing.lg,
  },
  cell: {
    paddingVertical: 4,
    paddingRight: 10,
  },
  label: {
    fontSize: pdfFontSize.xxs,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: pdfLetter.openLabel,
    color: pdfColors.textMuted,
    marginBottom: 2,
  },
  value: {
    fontSize: pdfFontSize.md,
    fontWeight: 600,
    color: pdfColors.textPrimary,
  },
  valueMono: {
    fontFamily: pdfFontFamily.mono,
  },
});

export const MetaBox: React.FC<MetaBoxProps> = ({ items, columns = 4 }) => {
  const widthPct = `${100 / columns}%`;
  return (
    <View style={metaStyles.card} wrap={false}>
      {items.map((it, i) => (
        <View key={i} style={[metaStyles.cell, { width: widthPct }] as any}>
          <Text style={metaStyles.label}>{it.label.toUpperCase()}</Text>
          {typeof it.value === 'string' || typeof it.value === 'number' ? (
            <Text style={it.mono ? [metaStyles.value, metaStyles.valueMono] : metaStyles.value}>
              {it.value === '' || it.value == null ? '—' : String(it.value)}
            </Text>
          ) : (
            it.value ?? <Text style={metaStyles.value}>—</Text>
          )}
        </View>
      ))}
    </View>
  );
};

export default KV;
